
import { ACCOUNT_TYPE, fetchUserData, selectUserData } from 'containers/Account/accountSlice'
import ConsentPreviewPopup from 'components/popups/ConsentPreview'
import { selectIsLoggedIn } from 'containers/Login/loginSlice'
import { useReduxDispatch, useReduxSelector } from 'store'
import { getPhoneParts, isValidPhone } from 'utils/phone'
import { initialExp, finalExp } from 'utils/validation'
import PhoneInput from 'components/inputs/PhoneInput'
import React, { useEffect, useState } from 'react'
import ArrowBtn from 'components/buttons/ArrowBtn'
import Checkbox from 'components/inputs/Checkbox'
import useTranslation from 'hooks/useTranslation'
import BackBtn from 'components/buttons/BackBtn'
import { useHistory } from 'react-router-dom'
import Popup from 'components/popups/Popup'
import Input from 'components/inputs/Input'
import { STATUS } from 'utils/constants'
import Loading from 'components/Loading'
import { POST } from 'rest/request'
import * as urls from 'rest/urls'
import '../style.scss'

interface AvailabilityNotificationPopupProps {
  name: string
  placeId: string
  groupHash: string
  variantHash: string
  companyName: string
}

interface AvailabilityNotificationFormProps extends AvailabilityNotificationPopupProps {
  userName?: string
  email?: string
  phone?: string
  prefix?: string
}

const AvailabilityNotificationPopup: React.FC<AvailabilityNotificationPopupProps> = (props) => {
  const { status = STATUS.idle, data } = useReduxSelector(state => selectUserData(state, ACCOUNT_TYPE.basic))
  const isLoggedIn = useReduxSelector(selectIsLoggedIn)

  const dispatch = useReduxDispatch()
  useEffect(() => {
    if (isLoggedIn) dispatch(fetchUserData())
  }, [dispatch, isLoggedIn])

  return (
    <Popup
      centered
      isLine={false}>
      {(status === STATUS.failed || status === STATUS.succeed || !isLoggedIn)
        ? <AvailabilityNotificationForm
          {...props}
          userName={data?.name}
          email={data?.email}
          phone={data?.phone}
          prefix={data?.prefix}
        />
        : <Loading />}
    </Popup>
  )
}

const AvailabilityNotificationForm: React.FC<AvailabilityNotificationFormProps> = (props) => {
  const [privacyPopup, setPrivacyPopup] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [errors, setErrors] = useState<basic.Errors>({})
  const [form, setForm] = useState({
    name: props?.userName || '',
    ...getPhoneParts(props?.phone, props?.prefix),
    email: props?.email || '',
    policyAccept: false
  })

  const { goBack } = useHistory()
  const { t } = useTranslation('mapDetails.availability')
  const changeHandler: React.ChangeEventHandler<HTMLInputElement> = e => {
    if (!e) return
    const { name, value } = e.target
    if (name === 'name' || (!value || value.match(initialExp.mail))) setForm({
      ...form,
      [name]: value
    })
  }

  const submit = async () => {
    if (isLoading) return
    setLoading(true)
    try {
      const body = {
        ...form,
        placeId: props.placeId,
        groupHash: props.groupHash,
        variantHash: props.variantHash,
      }
      const response = await POST(urls.warehousenotify, { body })
      if (response?.data) goBack()
      else {
        if (response?.errors) setErrors(response.errors)
        setLoading(false)
      }

    } catch (error) {
      setLoading(false)
    }
  }

  const validateForm = () => {
    const isMailOk = form.email.match(finalExp.mail)
    const isPhoneOk = isValidPhone(form.prefix, form.phone)
    if (isMailOk && isPhoneOk && form.name && form.policyAccept) {
      setErrors({})
      submit()

    } else {
      setErrors({
        name: form.name ? '' : t('fieldIsRequired'),
        email: form.email ? isMailOk ? '' : t('pleaseEnterCorrectly') : t('fieldIsRequired'),
        phone: form.phone ? isPhoneOk ? '' : t('pleaseEnterCorrectly') : t('fieldIsRequired'),
        policyAccept: form.policyAccept ? '' : t('fieldIsRequired')
      })
    }
  }

  return (
    <>
      {privacyPopup && <ConsentPreviewPopup
        type='privacy'
        close={() => {
          setPrivacyPopup(false)
        }}
      />}

      <div className='popup--availabilityNotification'>
        <div className='popup__title'>
          <div>{t('notifications')}</div>
          <div>{t('fillOutToReceiveInformationAboutTheAvailabilityOf')} {props.name}</div>
        </div>

        <Input
          required
          name='name'
          value={form.name}
          warning={errors.name}
          label={t('nameAndSurname')}
          placeholder={t('nameAndSurname')}
          changeHandler={changeHandler} />

        <PhoneInput
          required
          name='phone'
          value={form.phone}
          prefix={form.prefix}
          warning={errors.phone}
          label={t('phoneNumber')}
          placeholder={t('phoneNumber')}
          setValue={value => setForm(prev => ({ ...prev, ...value }))} />

        <Input
          required
          name='email'
          value={form.email}
          warning={errors.email}
          label={t('email')}
          placeholder={t('email')}
          changeHandler={changeHandler} />

        <div className={`checkbox__area input__default${errors.policyAccept ? ' --warning' : ''}`}>
          <label className='input__label'>
            <span className='--warning'>* </span>
            <span>{t('consents')}</span>
            {errors.policyAccept && <span className='label--warning'>
              - {errors.policyAccept}
            </span>}
          </label>

          <Checkbox
            name='policyAccept'
            checked={form.policyAccept}
            label={t('contentOfConsent')}
            changeHandler={() => {
              setForm(prev => ({ ...prev, policyAccept: !form.policyAccept }))
            }}
          />
        </div>

        <p>{t('controllerOfYourPersonalDataIs')} {props.companyName}. {t('yourDataWillBeProcessedSolelyForPurposeOfInformingYou')} {t('moreDetailsCanBeFoundIn')} <span className='--link' onClick={() => { setPrivacyPopup(true) }}>{t('privacyPolicy')}</span></p>

        <div className='popup__buttons'>
          <ArrowBtn
            enlarged
            text={t('confirm')}
            isLoading={isLoading}
            action={validateForm}
          />
          <BackBtn />
        </div>
      </div>
    </>
  )
}

export default AvailabilityNotificationPopup